// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'connections/AuthContext';
import routes_web from 'connections/routes_web';
import { useFinanceProfile } from './moduleServices/finance_profilecontext';
import { AssetsDataProvider } from './moduleServices/assets_data_context';

// imported components
import NavbarTop from 'views/finance/moduleComponents/NavbarTop';
import NavbarMiddle from 'views/finance/moduleComponents/NavbarMiddle';
import NavbarBottom from 'views/finance/moduleComponents/NavbarBottom';
import FinanceSetupBanner from './moduleComponents/FinanceSetupBanner';
import { useFinanceYearsRange } from './moduleServices/useFinanceYearsRange';
import { useScrollToTopOnChange } from 'services/useScrollToTopOnChange';
import TabFinanceFlow from './tab_flow/main_flow';
import TabFinanceExtras from './tab_extras/main_extras';
import TabFinanceOverview from './tab_overview/main_overview';
import TabFinanceSettings from './tab_settings/main_settings';
import BudgetMain from './tab_budget/main_budget';
import AssetsMain from './tab_assets/main_assets';

// ###### MAIN COMPONENT
const FinanceMain = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { profile, loading } = useFinanceProfile();
  const [activeTabL1, setActiveTabL1] = useState('overview');
  const [activeTabL2, setActiveTabL2] = useState('home');
  const [showSetupBanner, setShowSetupBanner] = useState(false);
  const yearsRange = useFinanceYearsRange();

  const assetsBootstrapEnabled =
    activeTabL2 === 'assets' || (activeTabL2 === 'home' && activeTabL1 === 'overview');

  // ### EFFECTS
  useEffect(() => {
    // Detect first time user (no profile)
    if (!loading && !profile) {
      setShowSetupBanner(true);
    }
  }, [loading, profile]);

  useEffect(() => {
    if (activeTabL2 === 'home') {
      setActiveTabL1('overview');
    } else if (activeTabL2 === 'flow' || activeTabL2 === 'budget' || activeTabL2 === 'assets') {
      setActiveTabL1('board');
    }
  }, [activeTabL2]);

  useScrollToTopOnChange(activeTabL2, activeTabL1);

  // ### HANDLERS
  const handleTabChange = (tab) => {
    setActiveTabL2(tab);
  };

  // ### RETURN VIEW
  return (
    <div className="min-h-screen flex flex-col bg-gray-100 p-2 pb-20">
      <AssetsDataProvider enabled={assetsBootstrapEnabled}>
        <div className="max-w-5xl w-full mx-auto pt-24">

          {/* Navbar Middle (Tab L1) - Do not render when in settings */}
          {activeTabL2 !== 'settings' && (
            <NavbarMiddle
              activeTabL1={activeTabL1}
              setActiveTabL1={setActiveTabL1}
              activeTabL2={activeTabL2}
            />
          )}

          {/* Tabs Content */}
          {activeTabL2 === 'home' && activeTabL1 === 'overview' && (
            <TabFinanceOverview
              onNavigateToFlow={() => {
                setActiveTabL2('flow');
                setActiveTabL1('board');
              }}
              onNavigateToBudget={() => {
                setActiveTabL2('budget');
                setActiveTabL1('board');
              }}
              onNavigateToAssets={() => {
                setActiveTabL2('assets');
                setActiveTabL1('board');
              }}
            />
          )}

          {activeTabL2 === 'flow' && (
            <TabFinanceFlow activeTabL1={activeTabL1} yearsRange={yearsRange} />
          )}

          {activeTabL2 === 'budget' && (
            <BudgetMain activeTabL1={activeTabL1} yearsRange={yearsRange} />
          )}

          {activeTabL2 === 'assets' && (
            <AssetsMain activeTabL1={activeTabL1} yearsRange={yearsRange} />
          )}

          {activeTabL2 === 'home' && activeTabL1 === 'extras' && (
            <TabFinanceExtras />
          )}

          {activeTabL2 === 'settings' && (
            <TabFinanceSettings />
          )}
        </div>
      </AssetsDataProvider>

      {/* Navbar Top */}
      <NavbarTop
        title="Finance"
        onHomeClick={() => navigate(routes_web.accounts_home)}
        onLogoutClick={logout}
        handleTabChange={handleTabChange}
        activeTabL2={activeTabL2}
      />

      {/* Navbar Bottom (Tab L2) */}
      <NavbarBottom activeTabL2={activeTabL2} handleTabChange={handleTabChange} />

      {/* Setup Banner - Renders on top of everything if profile doesn't exist */}
      {showSetupBanner && <FinanceSetupBanner onComplete={() => window.location.reload()} />}
    </div>
  );
};

export default FinanceMain;
